import React, { useState, useEffect } from 'react';
import { bool, func, object, number, string } from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage, intlShape } from '../../../../util/reactIntl';
import { propTypes } from '../../../../util/types';
import { trackEvent } from '../../../../util/analytics';
import {
  Avatar,
  CartMenu,
  InlineTextButton,
  LinkedLogo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  Modal,
  NamedLink,
  PrimaryButton,
  SecondaryButton,
  SearchField,
} from '../../../../components';
import { ReactComponent as NotificationIcon } from './Bell.svg';

const ChatBubbleOutlineIcon = ({ sx, style }) => {
  const size = sx?.fontSize || 20;
  const color = sx?.color || 'currentColor';
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={style}
      aria-hidden="true"
    >
      <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
    </svg>
  );
};
const ReceiptLongIcon = ({ sx, style }) => {
  const size = sx?.fontSize || 20;
  const color = sx?.color || 'currentColor';
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={color}
      style={style}
      aria-hidden="true"
    >
      <path d="M19.5 3.5L18 2l-1.5 1.5L15 2l-1.5 1.5L12 2l-1.5 1.5L9 2 7.5 3.5 6 2v16H3v3c0 1.66 1.34 3 3 3h12c1.66 0 3-1.34 3-3V2l-1.5 1.5zM19 21c0 .55-.45 1-1 1s-1-.45-1-1v-3H8V5h11v16zM9 7h6v2H9zm8 0h2v2h-2zm-8 4h6v2H9zm8 0h2v2h-2z" />
    </svg>
  );
};

import css from './TopbarDesktop.module.css';
import { Link } from 'react-router-dom/';
import { useDispatch } from 'react-redux';
import { markNotificationAsRead } from '../../../../ducks/user.duck';

const TopbarDesktop = props => {
  const {
    className,
    appConfig,
    currentUser,
    currentPage,
    rootClassName,
    currentUserHasListings,
    notificationCount,
    eventCount = 0,
    intl,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
    initialSearchFormValues,
    notifications = [],
    onManageDisableScrolling,
  } = props;
  const [mounted, setMounted] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isLogoutOpen, setIsLogoutOpen] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    setMounted(true);
  }, []);

  // Slim sticky variant kicks in after 80px of scroll (spec §3 "sticky state").
  // Uses a hysteresis band (80 → compress, 40 → expand) so the
  // height change itself — which shifts scrollY by ~16px — can't
  // bounce the state between normal and condensed when the user
  // parks right at the threshold. Without this, at scroll ≈80px:
  //   1. compress (72→56) → scrollY drops to ≈64 → below threshold
  //   2. expand  (56→72) → scrollY jumps to ≈80 → above threshold
  //   3. compress again… infinite flip.
  // Passive listener — no preventDefault needed.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const COMPRESS_AT = 80;
    const EXPAND_AT = 40;
    const onScroll = () => {
      const y = window.scrollY;
      setIsScrolled(prev => (prev ? y > EXPAND_AT : y > COMPRESS_AT));
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const marketplaceName = appConfig.marketplaceName;
  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

  const classes = classNames(rootClassName || css.root, className);

  const draftId = '00000000-0000-0000-0000-000000000000';
  const draftSlug = 'draft';

  // Unified SearchField primitive — wraps RegNumInput (default REG mode)
  // and the existing TopbarSearchForm (Text mode) behind a mode toggle.
  // Spec §5: REG is the default because it's the Wheelplace differentiator.
  const search = (
    <div className={css.searchSlot}>
      <SearchField
        size="compact"
        onSearchSubmit={onSearchSubmit}
        initialSearchFormValues={initialSearchFormValues}
        appConfig={appConfig}
      />
    </div>
  );


  // Inbox target tab — used by both the envelope cluster icon and the
  // notification-flyout "Se alla notiser" footer. Persists the last
  // buyer/seller mode the user chose in localStorage.
  let inboxTab = currentUserHasListings ? 'sales' : 'orders';
  if (typeof window !== 'undefined') {
    const mode = window.localStorage.getItem('buyerSellerMode');
    if (mode === 'buyer') inboxTab = 'orders';
    if (mode === 'seller') inboxTab = 'sales';
  }

  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;
  /* "Mark all as read" still operates on everything unread in the flyout, which is
   * the whole list — that is what the button says. The BADGE is what changed:
   * see below. */
  const unreadNotifications = notifications.filter(n => n.isUnread);

  // Swedish relative-time formatter (spec §6.2). "nu" / "5 min sedan" /
  // "3 tim sedan" / "Igår" / "3 dagar sedan" / absolute fallback for
  // anything older than a week.
  const formatRelativeSwedish = iso => {
    if (!iso) return null;
    const now = Date.now();
    const then = new Date(iso).getTime();
    const diffSec = Math.max(0, Math.round((now - then) / 1000));
    if (diffSec < 45) return 'Nu';
    const diffMin = Math.round(diffSec / 60);
    if (diffMin < 60) return `${diffMin} min sedan`;
    const diffHour = Math.round(diffMin / 60);
    if (diffHour < 24) return `${diffHour} tim sedan`;
    const diffDay = Math.round(diffHour / 24);
    if (diffDay === 1) return 'Igår';
    if (diffDay < 7) return `${diffDay} dagar sedan`;
    return new Date(iso).toLocaleDateString('sv-SE', {
      day: 'numeric',
      month: 'short',
    });
  };

  const handleMarkAllRead = () => {
    unreadNotifications.forEach(n => dispatch(markNotificationAsRead(n.id.uuid)));
  };

  const notificationsMenu = authenticatedOnClientSide ? (
    /* Same right-corner geometry as the profile menu — grow leftward, always. */
    <Menu contentPosition="left">
      <MenuLabel className={css.notificationMenuLabel}>
        {/* Bell icon — uses shared .iconSlot vocabulary (40×40, 8px
          * radius) matching heart + envelope. Dot badge when unread
          * per spec §5.4: notifications are "something changed", so a
          * dot is more honest than a count. */}
        {/* BR-6 / AC-5 — the bell counts EVENTS (a payment taken, a shipment, a
          * cancellation), never messages. It used to count everything unread,
          * which overlapped the envelope and produced two numbers that argued
          * with each other. */}
        <div
          className={css.iconSlot}
          aria-label={eventCount > 0 ? `Notiser, ${eventCount} nya händelser` : 'Notiser'}
          data-tooltip="Notiser"
          role="button"
          tabIndex={0}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
            <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
          </svg>
          {eventCount > 0 ? <span className={css.badgeDot} aria-hidden="true" /> : null}
        </div>
      </MenuLabel>
      <MenuContent className={css.notificationMenuContent}>
        {/* MenuContent rejects null children (strict validation in
          * React.Children.forEach). Build an array + .filter(Boolean)
          * so empty branches never reach the validator. */}
        {[
          <MenuItem key="notif-header">
            <div className={css.notificationHeader}>
              <span className={css.notificationHeaderTitle}>Notiser</span>
              {unreadNotifications.length > 0 ? (
                <button
                  type="button"
                  className={css.notificationMarkAll}
                  onClick={handleMarkAllRead}
                >
                  Markera alla som läst
                </button>
              ) : null}
            </div>
          </MenuItem>,
          ...(notifications.length > 0
            ? notifications.slice(0, 8).map(n => {
                // sortDate is set per notification in fetchNotificationData
                // (last message date for messages, createdAt for activity).
                const createdAt = n.sortDate || n.attributes.createdAt;
                const relative = formatRelativeSwedish(createdAt);
                return (
                  <MenuItem key={n.id.uuid} className={css.notificationMenuItem}>
                    <Link
                      to={n.url}
                      className={classNames(
                        css.notificationItem,
                        n.isUnread ? css.notificationItemUnread : css.notificationItemRead
                      )}
                      onClick={() => dispatch(markNotificationAsRead(n.id.uuid))}
                    >
                      {n.isUnread ? (
                        <span className={css.notificationUnreadDot} aria-hidden="true" />
                      ) : (
                        <span className={css.notificationUnreadDotSpacer} aria-hidden="true" />
                      )}
                      <div className={css.notificationIcon}>
                        {n.notificationType === 'MESSAGE' ? (
                          <ChatBubbleOutlineIcon sx={{ fontSize: 18, color: 'currentColor' }} />
                        ) : (
                          <ReceiptLongIcon sx={{ fontSize: 18, color: 'currentColor' }} />
                        )}
                      </div>
                      <div className={css.notificationText}>
                        <p className={css.notificationTitle}>{n.notificationTitle}</p>
                        {relative ? <p className={css.notificationDate}>{relative}</p> : null}
                      </div>
                    </Link>
                  </MenuItem>
                );
              })
            : [
                <MenuItem key="notif-empty">
                  <div className={css.notificationsEmpty}>
                    <span className={css.notificationsEmptyIcon} aria-hidden="true">
                      <svg
                        width="28"
                        height="28"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M13.73 21a2 2 0 01-3.46 0" />
                        <path d="M18.63 13A17.89 17.89 0 0118 8" />
                        <path d="M6.26 6.26A5.86 5.86 0 006 8c0 7-3 9-3 9h14" />
                        <path d="M18 8a6 6 0 00-9.33-5" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </svg>
                    </span>
                    <span className={css.notificationsEmptyTitle}>Inga notiser än</span>
                    <span className={css.notificationsEmptyLead}>
                      När något händer dyker det upp här.
                    </span>
                  </div>
                </MenuItem>,
              ]),
          notifications.length > 0 && (
            <MenuItem key="notif-footer">
              <NamedLink
                name="InboxPage"
                params={{ tab: inboxTab }}
                className={css.notificationsFooter}
              >
                <span>Se alla notiser</span>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M5 12h14M13 5l7 7-7 7" />
                </svg>
              </NamedLink>
            </MenuItem>
          ),
        ].filter(Boolean)}
      </MenuContent>
    </Menu>
  ) : null;

  // Text-only desktop nav. Icons dropped per §3 "text-first horizontal nav".
  const trackNav = destination => () => trackEvent('header_nav_click', { destination });

  // Account cluster icon buttons (spec §5.2) — shared 40×40 / 8px-radius
  // icon-slot. Muted default color, brand-blue on hover. Heart = saved,
  // envelope = messages. Both route directly (no flyout on this phase).
  const savedLink = authenticatedOnClientSide ? (
    <NamedLink
      className={css.iconSlot}
      name="ManageFavouritesPage"
      aria-label="Sparade"
      data-tooltip="Sparade"
      onClick={trackNav('saved')}
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />
      </svg>
    </NamedLink>
  ) : null;

  // Numeric-badge formatter per spec §5.3 — hide at 0, show actual
  // number 1–99, cap at "99+".
  const formatBadgeCount = n => {
    if (!n || n <= 0) return null;
    if (n > 99) return '99+';
    return String(n);
  };
  const inboxBadge = formatBadgeCount(notificationCount);

  /* BR-6 / AC-5 — the envelope counts UNREAD MESSAGES.
   *
   * `notificationCount` used to be `currentUserNotificationCount`: the number of
   * sales whose last transition needs provider attention. Events, on the
   * envelope. It now comes from the `{provider,customer}HasUnreadMessages`
   * metadata flags — the same fields `/inbox/{orders,sales}?filter=unread`
   * filters on, so the badge agrees with the page it opens. */
  const inboxLink = authenticatedOnClientSide ? (
    <NamedLink
      className={css.iconSlot}
      name="InboxPage"
      params={{ tab: inboxTab }}
      aria-label={
        notificationCount > 0
          ? `Meddelanden, ${notificationCount} olästa`
          : 'Meddelanden'
      }
      data-tooltip="Meddelanden"
      onClick={trackNav('inbox')}
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M4 4h16a2 2 0 012 2v12a2 2 0 01-2 2H4a2 2 0 01-2-2V6a2 2 0 012-2z" />
        <path d="M22 6l-10 7L2 6" />
      </svg>
      {inboxBadge ? (
        <span className={css.badgeCount} aria-hidden="true">
          {inboxBadge}
        </span>
      ) : null}
    </NamedLink>
  ) : null;

  const listingsLink = (
    <NamedLink name="SearchPage" className={css.navLink} onClick={trackNav('listings')}>
      <FormattedMessage id="Topbar.listingsTitle" />
    </NamedLink>
  );

  /* Bevakningar replaced Förfrågningar in the nav — P25009-371 BR-6/BR-8. */
  const savedSearchesLink = (
    <NamedLink name="SavedSearchesPage" className={css.navLink} onClick={trackNav('saved_searches')}>
      <FormattedMessage id="Topbar.savedSearchesTitle" />
    </NamedLink>
  );

  const contactLink = (
    <Link className={css.navLink} to="/p/kontakt" onClick={trackNav('contact')}>
      <FormattedMessage id="Topbar.contact" />
    </Link>
  );

  /* Avatar-dropdown row icons (Lucide-style inline SVGs). Kept inline
   * rather than extracted to a separate file — they're used only here
   * and inlining keeps the dropdown JSX self-documenting. */
  const MenuIconLogout = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
      <path d="M16 17l5-5-5-5" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );

  const displayName = currentUser?.attributes?.profile?.displayName;

  const profileMenu = authenticatedOnClientSide ? (
    /* contentPosition="left" = the dropdown grows LEFTWARD from the label,
     * always. Menu's default measures the content's width on open to pick a
     * side, and a reopen can measure the collapsed content (≈0 wide), anchor
     * the menu left and run it off the right edge of the screen. The avatar
     * is pinned to the bar's right corner, so measuring is pointless. */
    <Menu contentPosition="left">
      <MenuLabel
        className={css.profileMenuLabel}
        isOpenClassName={css.profileMenuIsOpen}
      >
        <span className={css.avatarTrigger} aria-label="Konto">
          <Avatar className={css.avatar} user={currentUser} disableProfileLink />
          <svg
            className={css.avatarChevron}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </span>
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        {/* MenuContent rejects null children — pass a filtered array so
          * conditional rows (business-only Subscriptions, Stripe) never
          * send null through React.Children.forEach. */}
        {[
          <MenuItem key="profileCard">
            <NamedLink name="ProfileSettingsPage" className={css.profileCard}>
              <Avatar className={css.profileCardAvatar} user={currentUser} disableProfileLink />
              <span className={css.profileCardText}>
                <span className={css.profileCardName}>{displayName || 'Konto'}</span>
                <span className={css.profileCardLink}>
                  Visa profil
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M9 5l7 7-7 7" />
                  </svg>
                </span>
              </span>
            </NamedLink>
          </MenuItem>,

          /* P25009-431 BR-1: the dropdown is identity only — everything it used
           * to duplicate (Dina annonser, Favoriter, Inköp, Statistik, the KONTO
           * rows, Kontakt, the two admin dashboards) lives in the one account
           * rail (sellerNavGroups.js), the private-profile tabs, or — for the
           * admin dashboards — the tab strip on the Statistik page
           * (AdminStatsTabs). */
          <MenuItem key="divider4"><span className={css.menuDivider} /></MenuItem>,

          <MenuItem key="logout">
            <InlineTextButton
              rootClassName={css.menuRowLogout}
              onClick={() => setIsLogoutOpen(true)}
            >
              <span className={css.menuRowIcon}><MenuIconLogout /></span>
              <span className={css.menuRowLabel}>
                <FormattedMessage id="TopbarDesktop.logout" />
              </span>
            </InlineTextButton>
          </MenuItem>,
        ].filter(Boolean)}
      </MenuContent>
    </Menu>
  ) : null;

  // const signupLink = isAuthenticatedOrJustHydrated ? null : (
  //   <NamedLink name="SignupPage" className={css.signupLink}>
  //     <span className={css.signup}>
  //       <FormattedMessage id="TopbarDesktop.signup" />
  //     </span>
  //   </NamedLink>
  // );

  // Login — plain text with a small user icon to the right (spec §3, logged-out
  // state). No border, no box — no longer competing with the primary CTA.
  const loginLink = isAuthenticatedOrJustHydrated ? null : (
    <NamedLink
      name="LoginPage"
      className={css.loginLink}
      onClick={() => trackEvent('login_open', { trigger: 'header' })}
    >
      <span>
        <FormattedMessage id="TopbarDesktop.login" />
      </span>
      <svg
        className={css.loginIcon}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"
        />
      </svg>
    </NamedLink>
  );

  const plusIcon = (
    <svg className={css.pillIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
    </svg>
  );

  const createListingLabel = intl.formatMessage({ id: 'TopbarDesktop.createListing' });
  const createListing = (
    <NamedLink
      className={css.pillPrimary}
      name="WheelplaceCreateListingPage"
      aria-label={createListingLabel}
      data-tooltip={createListingLabel}
      onClick={() =>
        trackEvent('header_cta_click', { variant: isScrolled ? 'sticky' : 'landing' })
      }
    >
      {plusIcon}
      <span>{createListingLabel}</span>
    </NamedLink>
  );

  // Note: the separate "Skapa förfrågan" header link has been removed —
  // the Förfrågningar nav item already routes to the buy-side surface,
  // and a proper primary CTA for creating a request lives on that page.
  // "Skapa förfrågan" is still surfaced in the mobile drawer for deep
  // discovery (TopbarMobileMenu.newSearchLink). Keeping the header
  // focused on ONE primary CTA (+ Lägg in annons) per spec §2.

  const navClasses = classNames(classes, { [css.isScrolled]: isScrolled });

  return (
    <nav className={navClasses}>
      <div className={css.inner}>
        {/* Group 1 — Brand, anchored flush to viewport left. */}
        <div className={css.leftGroup}>
          <LinkedLogo
            className={css.logoLink}
            layout="desktop"
            alt={intl.formatMessage({ id: 'TopbarDesktop.logo' }, { marketplaceName })}
          />
        </div>

        {/* Group 2 — Interactive content layer. Capped at
          * contentMaxWidth and centered between Logo and cluster so
          * search + nav + CTA align with the page content below.
          * Within this group: search on the left edge, nav + CTA
          * on the right edge. */}
        <div className={css.middleGroup}>
          <div className={css.middleLeft}>{search}</div>
          <div className={css.middleRight}>
            <div className={css.navList}>
              {listingsLink}
              {savedSearchesLink}
              {contactLink}
            </div>
            {createListing}
          </div>
        </div>

        {/* Group 3 — Account cluster, anchored flush to viewport
          * right. Divider (only when auth'd) separates the centered
          * action column from this edge-anchored cluster. */}
        <div className={css.rightGroup}>
          {authenticatedOnClientSide ? (
            <span className={css.clusterDivider} aria-hidden="true" />
          ) : null}
          <div className={css.accountCluster}>
            <CartMenu />
            {savedLink}
            {inboxLink}
            {notificationsMenu}
            {profileMenu}
            {loginLink}
          </div>
        </div>
      </div>

      {/* Logout confirmation — Modal lives at the nav root so it's
        * portaled above everything else. `onManageDisableScrolling`
        * is required by the Modal primitive; we thread it from
        * Topbar → TopbarDesktop via props. */}
      {onManageDisableScrolling ? (
        <Modal
          id="TopbarDesktop.logoutConfirm"
          isOpen={isLogoutOpen}
          onClose={() => setIsLogoutOpen(false)}
          usePortal
          onManageDisableScrolling={onManageDisableScrolling}
        >
          <div className={css.logoutModal}>
            <h2 className={css.logoutModalTitle}>Logga ut?</h2>
            <p className={css.logoutModalLead}>
              Du loggas ut från ditt konto och skickas tillbaka till startsidan.
            </p>
            <div className={css.logoutModalActions}>
              <SecondaryButton
                className={css.logoutModalCancel}
                onClick={() => setIsLogoutOpen(false)}
              >
                Avbryt
              </SecondaryButton>
              <PrimaryButton
                className={css.logoutModalConfirm}
                onClick={() => {
                  setIsLogoutOpen(false);
                  onLogout();
                }}
              >
                Logga ut
              </PrimaryButton>
            </div>
          </div>
        </Modal>
      ) : null}
    </nav>
  );
};

TopbarDesktop.defaultProps = {
  rootClassName: null,
  className: null,
  currentUser: null,
  currentPage: null,
  notificationCount: 0,
  initialSearchFormValues: {},
  appConfig: null,
};

TopbarDesktop.propTypes = {
  rootClassName: string,
  className: string,
  currentUserHasListings: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentPage: string,
  isAuthenticated: bool.isRequired,
  onLogout: func.isRequired,
  onManageDisableScrolling: func,
  notificationCount: number,
  onSearchSubmit: func.isRequired,
  initialSearchFormValues: object,
  intl: intlShape.isRequired,
  appConfig: object,
};

export default TopbarDesktop;
